import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, isWorkspaceMismatch } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Loader2,
  Users,
  Mail,
  Trash2,
  Copy,
  Check,
  ShieldAlert,
} from "lucide-react";

const ROLE_LABEL = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
};

const ROLE_HELP = {
  owner: "Full control. Cannot be removed.",
  admin: "Can manage members and use all features.",
  member: "Can use all product features.",
};

/**
 * Team management for the CURRENT workspace.
 *
 * ── Why this file is careful about tenancy (Phase 5.7) ──────────────────────
 *
 * Reproduced before the fix: fetch workspace A's roster, switch to B, click
 * Remove → `200 {"ok": true}` and the membership was deleted from **B**. The
 * user removed a teammate from a workspace whose roster they were not looking
 * at, and the app reported success.
 *
 * Two independent causes, both fixed here:
 *
 *  1. The list was fetched once at mount (`[]` deps) and never refetched, so
 *     after a switch the rows on screen belonged to the previous tenant. Now
 *     the fetch is keyed on `workspaceEpoch` — a switch re-runs it exactly as
 *     the old full page reload did.
 *
 *  2. Every mutation targeted `/workspaces/current/...` — "current" as resolved
 *     server-side, at the moment the request lands, which is NOT necessarily
 *     the workspace whose roster produced the button. `GET .../members` returns
 *     no workspace identity at all, so the client could not even detect this.
 *     Now each mutation STATES the workspace it believes it is acting on via
 *     `X-Workspace-Id`, and `loadedFor` records which workspace produced the
 *     rows. Since the GET carries the same header, `loadedFor` is a fact rather
 *     than an assumption: the server either used that workspace or returned 409.
 *
 * `guardTenant` is the primary defence and the header is the backstop. The
 * guard closes the window where the rows are stale; the header closes the much
 * narrower window where a switch lands between the guard and the wire, by
 * making the request act on the tenant the user was actually looking at (or be
 * refused with a 409 if that tenant is no longer reachable). Neither one alone
 * is sufficient, and a destructive action deserves both.
 */
export default function TeamTab() {
  const { workspace, workspaceEpoch, wsSwitching } = useAuth();
  const wsId = workspace?.workspace_id || null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null); // {accept_url, email}
  const [copiedLink, setCopiedLink] = useState(false);
  // The workspace the rows CURRENTLY ON SCREEN were fetched for. Set only on a
  // successful load, so it is never a guess about what the user is looking at.
  // Read by every mutation; never rendered.
  const loadedForRef = useRef(null);
  // Sequence number, so a slow reply for A cannot repaint over B's roster.
  const seqRef = useRef(0);

  const load = useCallback(async (target) => {
    if (!target) return;
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    let mismatched = false;
    try {
      const r = await api.get("/workspaces/current/members", {
        headers: { "X-Workspace-Id": target },
      });
      if (seq !== seqRef.current) return; // superseded by a later load
      loadedForRef.current = target;
      setData(r.data);
    } catch (e) {
      if (seq !== seqRef.current) return;
      // Mid-repair, not broken — the interceptor is re-syncing and the epoch
      // bump will re-run this. Hold the loader rather than claim a failure.
      if (isWorkspaceMismatch(e)) { mismatched = true; return; }
      setError(e?.response?.data?.detail || "Could not load team.");
    } finally {
      if (seq === seqRef.current && !mismatched) setLoading(false);
    }
  }, []);

  // Keyed on the epoch AND the id: the epoch is what says "different tenant
  // now", the id is what we fetch. Clearing `data` first is the fail-closed
  // half — between the two tenants there are no rows at all, so there is no
  // window in which A's roster is on screen with B's Remove buttons.
  useEffect(() => {
    loadedForRef.current = null;
    setData(null);
    load(wsId);
  }, [wsId, workspaceEpoch, load]);

  /**
   * Refuse any mutation whose target tenant is no longer the one on screen.
   *
   * Returns the workspace id to act on, or null if the caller must stop. A
   * refusal is not an error the user caused, so it re-loads and explains rather
   * than firing a red toast.
   */
  const guardTenant = () => {
    const shownFor = loadedForRef.current;
    if (wsSwitching) {
      toast.info("Still switching workspace — try that again in a second.");
      return null;
    }
    if (!shownFor || !wsId || shownFor !== wsId) {
      // eslint-disable-next-line no-console
      console.warn("[team] refused a mutation across tenants", { shownFor, wsId });
      toast.error("This team list is from a different workspace. Reloading it now.");
      loadedForRef.current = null;
      setData(null);
      load(wsId);
      return null;
    }
    return shownFor;
  };

  const invite = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    const target = guardTenant();
    if (!target) return;
    setInviting(true);
    try {
      const r = await api.post(
        "/workspaces/current/members/invite",
        { email: email.trim(), role },
        { headers: { "X-Workspace-Id": target } }
      );
      setLastInvite({ accept_url: r.data.accept_url, email: r.data.email });
      setEmail("");
      toast.success(`Invite sent to ${r.data.email}`);
      await load(target);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not invite.");
    } finally {
      setInviting(false);
    }
  };

  const revokeInvite = async (inv) => {
    const target = guardTenant();
    if (!target) return;
    if (!window.confirm(`Revoke invite to ${inv.email}?`)) return;
    // Re-check AFTER the confirm dialog: it is modal to the page but not to the
    // app, and it can sit open for as long as the user likes.
    if (guardTenant() !== target) return;
    try {
      await api.delete(`/workspaces/current/members/invite/${inv.invite_id}`, {
        headers: { "X-Workspace-Id": target },
      });
      toast.success("Invite revoked.");
      await load(target);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke.");
    }
  };

  const removeMember = async (m) => {
    const target = guardTenant();
    if (!target) return;
    const label = workspace?.name ? ` from ${workspace.name}` : " from the workspace";
    if (!window.confirm(`Remove ${m.email || m.name}${label}?`)) return;
    if (guardTenant() !== target) return;
    try {
      await api.delete(`/workspaces/current/members/${m.user_id}`, {
        headers: { "X-Workspace-Id": target },
      });
      toast.success("Member removed.");
      await load(target);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove.");
    }
  };

  const changeRole = async (m, newRole) => {
    if (m.role === "owner") return;
    if (newRole === m.role) return;
    const target = guardTenant();
    if (!target) return;
    try {
      await api.patch(
        `/workspaces/current/members/${m.user_id}`,
        { role: newRole },
        { headers: { "X-Workspace-Id": target } }
      );
      toast.success(`${m.name || m.email} is now ${ROLE_LABEL[newRole]}.`);
      await load(target);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role.");
    }
  };

  const copyInviteLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      toast.success("Invite link copied.");
      setTimeout(() => setCopiedLink(false), 1500);
    } catch {
      toast.error("Could not copy — copy manually.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-lm-text2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading team…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="max-w-4xl" data-testid="team-tab">
      {/* Invite form */}
      <div className="bg-lm-surface border border-lm-border rounded-lg p-6 md:p-8 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-lm-tint2 flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4 text-lm-primary" />
          </div>
          <div>
            <h2 className="text-lg font-display font-semibold">Invite teammates</h2>
            <p className="text-sm text-lm-text2">
              They&rsquo;ll get access to the same brands, scans and leads. Send them the link
              below — invite expires in 14 days.
            </p>
          </div>
        </div>
        <form
          onSubmit={invite}
          className="flex flex-col sm:flex-row gap-2"
          data-testid="team-invite-form"
        >
          <input
            data-testid="team-invite-email"
            type="email"
            placeholder="teammate@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 border border-lm-border rounded-lg px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
          />
          <select
            data-testid="team-invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="border border-lm-border rounded-lg px-3 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="submit"
            disabled={inviting || !email.trim()}
            data-testid="team-invite-submit"
            className="inline-flex items-center gap-2 bg-lm-primary text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-lm-primaryHover disabled:opacity-60"
          >
            {inviting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Mail className="w-4 h-4" />
            )}
            Send invite
          </button>
        </form>
        {lastInvite && (
          <div
            className="mt-4 p-4 rounded-lg border border-amber-200 bg-amber-50/60"
            data-testid="team-invite-link-panel"
          >
            <div className="flex items-start gap-2 mb-2">
              <ShieldAlert className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <div className="text-sm text-lm-text">
                <span className="font-semibold">Share this link with {lastInvite.email}.</span>{" "}
                They need to sign in with that email to accept. Also shown once — copy it now.
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <code className="flex-1 text-xs font-mono bg-white border border-amber-200 rounded px-2.5 py-2 truncate">
                {lastInvite.accept_url}
              </code>
              <button
                onClick={() => copyInviteLink(lastInvite.accept_url)}
                data-testid="team-copy-invite-link"
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-amber-300 hover:bg-amber-100 rounded"
              >
                {copiedLink ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedLink ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pending invites */}
      {data.invites?.length > 0 && (
        <div className="bg-lm-surface border border-lm-border rounded-lg mb-6">
          <div className="px-5 py-4 border-b border-lm-border">
            <h3 className="text-sm font-display font-semibold">
              Pending invites ({data.invites.length})
            </h3>
          </div>
          <ul className="divide-y divide-lm-border">
            {data.invites.map((inv) => (
              <li
                key={inv.invite_id}
                data-testid="team-pending-invite"
                className="px-5 py-3 flex items-center justify-between gap-3 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{inv.email}</div>
                  <div className="text-xs text-lm-text3">
                    {ROLE_LABEL[inv.role]} · expires{" "}
                    {new Date(inv.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => revokeInvite(inv)}
                  data-testid="team-revoke-invite"
                  className="text-xs font-medium text-red-600 hover:bg-red-50 rounded px-2.5 py-1.5"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Members list */}
      <div
        className="bg-lm-surface border border-lm-border rounded-lg"
        data-testid="team-members-list"
      >
        <div className="px-5 py-4 border-b border-lm-border flex items-center gap-2">
          <Users className="w-4 h-4 text-lm-primary" />
          <h3 className="text-sm font-display font-semibold">
            Members ({data.members.length})
          </h3>
        </div>
        <ul className="divide-y divide-lm-border">
          {data.members.map((m) => (
            <li
              key={m.user_id}
              data-testid="team-member-row"
              className="px-5 py-3 flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-full bg-lm-tint flex items-center justify-center overflow-hidden shrink-0">
                {m.picture ? (
                  <img src={m.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-semibold text-lm-primary">
                    {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                <div className="text-xs text-lm-text3 truncate">{m.email}</div>
              </div>
              <select
                data-testid={`team-role-select-${m.user_id}`}
                value={m.role}
                disabled={m.role === "owner"}
                onChange={(e) => changeRole(m, e.target.value)}
                className="border border-lm-border rounded px-2 py-1 text-xs bg-white disabled:bg-lm-tint disabled:text-lm-text2"
                title={ROLE_HELP[m.role]}
              >
                {(data.role_options || ["owner", "admin", "member"]).map((r) => (
                  <option key={r} value={r} disabled={r === "owner" && m.role !== "owner"}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
              {m.role !== "owner" && (
                <button
                  onClick={() => removeMember(m)}
                  data-testid={`team-remove-${m.user_id}`}
                  className="p-1.5 text-lm-text3 hover:text-red-600 rounded"
                  aria-label={`Remove ${m.email}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
